import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Award, ChevronRight } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Epaulette } from "@/components/ranks/Epaulette";
import { getRanks, REGISTER_URL } from "@/lib/api";
import { Link } from "react-router-dom";

export default function Ranks() {
  const [ranks, setRanks] = useState([]);

  useEffect(() => {
    getRanks().then(setRanks).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0B10]" data-testid="ranks-page">
      <Navbar />
      <main className="mx-auto max-w-5xl px-5 pb-24 pt-28 lg:px-10 lg:pt-32">
        <div className="max-w-2xl">
          <p className="text-overline flex items-center gap-2 text-[#8A1538]">
            <Award className="h-4 w-4" /> Career Path
          </p>
          <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-[#FDFBF7] lg:text-5xl [text-wrap:balance]">
            The Pilot Rank System
          </h1>
          <p className="mt-4 text-base text-[#A1A1AA] [text-wrap:pretty]">
            Climb the ranks by logging block hours. Every promotion earns you a
            new set of epaulettes — from your first stripe as a trainee to the
            four bars and wings of a Training Captain.
          </p>
        </div>

        <div className="mt-12 space-y-4">
          {ranks.map((r, i) => (
            <motion.div
              key={r.name}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              data-testid={`rank-card-${i}`}
              className="flex items-center gap-6 rounded-2xl border border-white/5 bg-[#12141D] p-5 transition-colors duration-300 hover:border-[#D4AF37]/30 sm:p-6"
            >
              <div className="flex-shrink-0">
                <Epaulette stripes={r.stripes} wings={r.wings} stars={r.stars} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-mono-data text-xs text-[#8A1538]">RANK {i + 1}</span>
                  <h2 className="font-display text-xl font-bold text-[#FDFBF7] md:text-2xl">{r.name}</h2>
                </div>
                <p className="mt-1.5 text-sm text-[#A1A1AA] [text-wrap:pretty]">{r.blurb}</p>
              </div>
              <div className="flex-shrink-0 text-right">
                <p className="whitespace-nowrap font-mono-data text-lg font-bold text-[#D4AF37]">
                  {r.hours === 0 ? "Start" : `${r.hours.toLocaleString()}h`}
                </p>
                <p className="text-[11px] text-[#A1A1AA]">min. hours</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-start gap-4 rounded-2xl border border-[#8A1538]/30 bg-[#8A1538]/10 p-7 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-display text-xl font-bold text-[#FDFBF7]">Start earning your stripes</h3>
            <p className="mt-1 text-sm text-[#A1A1AA]">Join the roster and log your first flight today.</p>
          </div>
          <Link
            to={REGISTER_URL}
            data-testid="ranks-register-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#8A1538] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#A81A45]"
          >
            Become a Pilot <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
    </div>
  );
}
